import type { EntityRefLite } from './types';

// The no-code entity whose SYNCHRONOUS execution is currently running — a page function today.
// Emit sites (useScriptLog, useReportActionError) read it to attribute a blockless log/error to the
// function that produced it, instead of the generic 'log' fallback. Sync-only by design: set/restore
// brackets a synchronous body, so it is exact for page-function eval and never spans an `await`.
let currentOwner: EntityRefLite | undefined;

export function getPulseOwner(): EntityRefLite | undefined {
  return currentOwner;
}

/**
 * Runs `fn` with `owner` as the current pulse owner, restoring the previous owner afterwards — even
 * if `fn` throws. Save/restore (not a growing stack) keeps nested synchronous calls LIFO-correct:
 * function A → `actions...trigger()` → function B sets B, and B's restore hands A back.
 *
 * Callers gate on isPulseOn() before building `owner` and calling this, so the runner (pulse off)
 * allocates nothing — the free-when-off rule.
 */
export function withPulseOwner<T>(owner: EntityRefLite, fn: () => T): T {
  const previousOwner = currentOwner;
  currentOwner = owner;
  try {
    return fn();
  } finally {
    currentOwner = previousOwner;
  }
}

export const __resetPulseOwnerForTest = (): void => {
  currentOwner = undefined;
};
